import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api/dispatch`;

/** On/off and which env settings are still missing */
const getStatus = () => axios.get(`${API_URL}/status`);

/** Timers in minutes */
const getSettings = () => axios.get(`${API_URL}/settings`);
const updateSettings = (data) => axios.patch(`${API_URL}/settings`, data);

/** Buyers (คนกด) with a LINE group, their dispatch fields and onlineNow */
const getBuyers = () => axios.get(`${API_URL}/buyers`);
const updateBuyer = (id, data) => axios.patch(`${API_URL}/buyers/${id}`, data);

/** On / off for today, whatever their hours say (until the end of their hours or 02:00) */
const openToday = (id) => axios.post(`${API_URL}/buyers/${id}/open-today`);
const closeToday = (id) => axios.post(`${API_URL}/buyers/${id}/close-today`);

/** One sample card to the buyer's Chatcone ID. The header lets a dev machine send (ignored in production). */
const sendTestCard = (id) => axios.post(`${API_URL}/buyers/${id}/test-card`, {}, { headers: { 'x-outbound-allow': 'chatcone' } });

/** New personal link for the buyer (the old one stops working) → { token } */
const makeLink = (id) => axios.post(`${API_URL}/buyers/${id}/link`);

/** Buyer's own page (public, the token is the key) */
const getMe = (token) => axios.get(`${API_URL}/me/${token}`);
const meStart = (token) => axios.post(`${API_URL}/me/${token}/open`);
const meStop = (token) => axios.post(`${API_URL}/me/${token}/close`);
/** Shift buyers save their own next 7 days: { "YYYY-MM-DD": [{ start, end }] } */
const saveMyShifts = (token, shifts) => axios.put(`${API_URL}/me/${token}/shifts`, { shifts });

/** Buyer's tracking queue (public, same personal token) */
const getMyTracking = (token) => axios.get(`${API_URL}/me/${token}/tracking`);
const saveMyTracking = (token, orderId, body) => axios.post(`${API_URL}/me/${token}/tracking/${orderId}`, body);
const snoozeMyTracking = (token, orderId) => axios.post(`${API_URL}/me/${token}/tracking/${orderId}/snooze`);
const readMyScreenshot = (token, orderId, file) => {
  const form = new FormData();
  form.append('images', file);
  return axios.post(`${API_URL}/me/${token}/tracking/${orderId}/read`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

/** Buyer's proof page (public, the card's offer token is the key) */
const getProof = (token) => axios.get(`${API_URL}/proof/${token}`);
const postProof = (token, file) => {
  const form = new FormData();
  form.append('images', file);
  return axios.post(`${API_URL}/proof/${token}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

/** The address a buyer opens on their phone */
const buyerLinkUrl = (token) => `${window.location.origin}/dispatch/me/${token}`;

/** "ตอนนี้ใครรับงาน": every buyer × channel — a card would reach them now, or the reason it would not */
const getNow = () => axios.get(`${API_URL}/now`);
/** "สรุปคนกด": per day and per buyer. from / to = service days, YYYY-MM-DD */
/** Each buyer's balance from their Google Sheet (kept 5 minutes on the server unless refresh) */
const getBalances = (refresh = false) => axios.get(`${API_URL}/report/balances`, { params: refresh ? { refresh: 1 } : {} });
const getReport = ({ from, to, shop = null }) => axios.get(`${API_URL}/report`, { params: { from, to, ...(shop ? { shop } : {}) } });
// One buyer's cards over the range: the detail popup of แยกตามคนกด.
const getReportBuyer = (id, { from, to, shop = null }) => axios.get(`${API_URL}/report/buyers/${id}`, { params: { from, to, ...(shop ? { shop } : {}) } });
/** Admin board: jobs that need a person, with every card (who opened what) */
const getAlerts = (shop = null) => axios.get(`${API_URL}/alerts`, { params: shop ? { shop } : {} });
/** Every job (board list): { status?, shop?, hours? } — hours = created in the last N hours */
const getJobs = ({ status = null, shop = null, hours = null } = {}) => axios.get(`${API_URL}/jobs`, {
  params: { ...(status ? { status } : {}), ...(shop ? { shop } : {}), ...(hours ? { hours } : {}) },
});
/** Task handled (nothing to record on the order) */
const acknowledge = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/acknowledge`);
/** The proof was copied to the customer's chat — the "แจ้งลูกค้า" task closes */
const informed = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/informed`);
/** Stop the bot job only — the order is untouched */
const cancelJob = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/cancel`);
/** The order enters the cancel tab (like an admin cancel); the job ends */
const cancelOrder = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/cancel-order`);
/** The chased buyer did not buy: close their card, next buyer */
const passToNext = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/pass`);
// "nobody took it" task → the job waits and cards go out again at the next 07:00
const deferToMorning = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/defer`);
/** Board: a reviewed site purchase request goes to the buyers (the admin must hold the case) */
/** pinnedOrderByOptionId: "ส่งการ์ดให้คนนี้เท่านั้น" — that buyer only, no queue; null = the queue */
const sendPurchaseToBot = (purchaseId, pinnedOrderByOptionId = null) => axios.post(`${API_URL}/purchases/${purchaseId}/send`, pinnedOrderByOptionId ? { pinnedOrderByOptionId } : {});
/** Purchase-request job: ซื้อไม่ทัน → the board's reject (exact wallet refund + customer LINE), by the admin's click */
const refundPurchase = (jobId, remarks = null) => axios.post(`${API_URL}/jobs/${jobId}/refund-purchase`, { remarks });
/** Force: the card goes to this buyer now, rules ignored; the job is theirs from then on */
const forceSend = (jobId, orderByOptionId) => axios.post(`${API_URL}/jobs/${jobId}/force-send`, { orderByOptionId });
/** Hold a task ("รับงาน"); 409 with the holder when taken, { takeover: true } steals it */
const claimJob = (jobId, takeover = false) => axios.post(`${API_URL}/jobs/${jobId}/claim`, { takeover });
const releaseJob = (jobId) => axios.post(`${API_URL}/jobs/${jobId}/release`);
/** Task closed with a purchase: proof image + who bought it (order → that buyer + fee) */
const postJobProof = (jobId, file, orderByOptionId = null) => {
  const form = new FormData();
  form.append('images', file);
  if (orderByOptionId != null) form.append('orderByOptionId', String(orderByOptionId));
  return axios.post(`${API_URL}/jobs/${jobId}/proof`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export default {
  getAlerts,
  getNow,
  getReport,
  getReportBuyer,
  getBalances,
  postJobProof,
  getJobs,
  acknowledge,
  informed,
  cancelJob,
  cancelOrder,
  passToNext,
  deferToMorning,
  sendPurchaseToBot,
  refundPurchase,
  forceSend,
  claimJob,
  releaseJob,
  getStatus,
  getSettings,
  updateSettings,
  getBuyers,
  updateBuyer,
  openToday,
  closeToday,
  makeLink,
  sendTestCard,
  getMe,
  meStart,
  meStop,
  saveMyShifts,
  getMyTracking,
  saveMyTracking,
  snoozeMyTracking,
  readMyScreenshot,
  getProof,
  postProof,
  buyerLinkUrl,
};
